import { ElementRef, Inject, Injectable, InjectionToken, Provider } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilKeyChanged, map } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';

import { RtPlatform } from '../core/platform/platform';

export interface IIntersectionObserverAreaConfig {
  debounceTime?: number;
  // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
  intersectionOptions?: {
    threshold: number | number[];
    rootMargin: string;
  };
}

export const IntersectionObserverAreaConfig = new InjectionToken<IIntersectionObserverAreaConfig>(
  'IntersectionObserverAreaConfig',
);

/**
 * Provide this class in component which will act as visibility bounds for children (observed) components.
 *
 * Read more: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Creating_an_intersection_observer
 *
 * TODO: for ease of use, we might want to wrap this in component:
 * (<rt-intersection-observer-area></rt-intersection-observer-area>)
 */

@Injectable()
export class IntersectionObserverArea {
  public serviceAvailable: boolean;

  private intersection: IntersectionObserver;
  private entries: Subject<IntersectionObserverEntry[]> = new Subject();
  private observedElementsMap: WeakMap<
    Element,
    Observable<IntersectionObserverEntry>
  > = new WeakMap();

  constructor(
    private ref: ElementRef,
    private platform: RtPlatform,
    @Inject(IntersectionObserverAreaConfig) private config: IIntersectionObserverAreaConfig,
  ) {
    this.serviceAvailable = this.checkFeatureAvailability();
    if (this.serviceAvailable) {
      this.intersection = new IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          this.entries.next(entries);
        },
        {
          root: ref.nativeElement,
          ...config.intersectionOptions,
        },
      );
    }
  }

  public intersectionChange(el: ElementRef): Observable<IntersectionObserverEntry> {
    const observable$ = this.observeElement(el.nativeElement).pipe(
      distinctUntilKeyChanged('isIntersecting'),
    );

    return this.config.debounceTime! > 0
      ? observable$.pipe(debounceTime(this.config.debounceTime!))
      : observable$;
  }

  public disconnect(): void {
    if (this.intersection) {
      this.intersection.disconnect();
    }
  }

  private observeElement(el: Element): Observable<IntersectionObserverEntry> {
    if (this.observedElementsMap.has(el)) {
      return this.observedElementsMap.get(el)!;
    }

    this.intersection.observe(el);

    const intersectionEntry$ = new Observable<IntersectionObserverEntry>(observer => {
      const sub = this.entries
        .pipe(
          map(entries => entries.find(entry => entry.target === el)),
          filterBoolean,
        )
        .subscribe(observer);

      return () => {
        this.unobserve(el);
        sub.unsubscribe();
      };
    });

    this.observedElementsMap.set(el, intersectionEntry$);

    return intersectionEntry$;
  }

  private unobserve(el: Element): void {
    this.intersection.unobserve(el);
  }

  private checkFeatureAvailability(): boolean {
    // https://caniuse.com/#feat=intersectionobserver

    try {
      if ('IntersectionObserver' in window) {
        if (this.platform.EDGE) {
          const [_, major] = this.platform.ua.match(/Edge\/(\d+)/)!;
          return parseInt(major, 10) > 15;
        }

        if (this.platform.CHROME) {
          const [_, major] = this.platform.ua.match(/Chrome\/(\d+)/)!;
          return parseInt(major, 10) > 57;
        }

        if (this.platform.OPERA) {
          const [_, major] = this.platform.ua.match(/OPR\/(\d+)/)!;
          return parseInt(major, 10) > 44;
        }

        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  }
}

export const defaultIntersectionObserverAreaConfig = {
  debounceTime: 0,
  intersectionOptions: {
    rootMargin: '0px',
    threshold: [0.0, 1.0],
  },
};

export function provideIntersectionObserverArea(
  config: IIntersectionObserverAreaConfig = {},
): Provider[] {
  return [
    {
      provide: IntersectionObserverAreaConfig,
      useValue: {
        ...defaultIntersectionObserverAreaConfig,
        ...config,
      },
    },
    IntersectionObserverArea,
  ];
}
